import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet';
import {
  Inbox,
  Sparkles,
  MessageSquare,
  Handshake,
  Megaphone,
  Star,
  Briefcase,
  CornerDownRight,
  Circle,
  Lock
} from 'lucide-react';

const filters = [
  { value: 'all', label: 'All', icon: Inbox },
  { value: 'messages', label: 'Messages', icon: MessageSquare },
  { value: 'requests', label: 'Requests', icon: Handshake },
  { value: 'announcements', label: 'Announcements', icon: Megaphone }
];

const previewRows = [
  {
    id: 'r1',
    creator: 'PHOENYX',
    handle: '@phoenyx',
    avatarColor: 'pink',
    preview: 'Feature Request — Multi-creator livestream scheduling',
    type: { label: 'Feature Request', icon: Star, color: 'amber' },
    status: { label: 'Needs Review', color: 'amber' },
    timestamp: '2h'
  },
  {
    id: 'r2',
    creator: 'Maya R.',
    handle: '@mayar',
    avatarColor: 'purple',
    preview: 'Collaboration Invite — Joint creator challenge for the 7-day streak',
    type: { label: 'Collab Invite', icon: Handshake, color: 'indigo' },
    status: { label: 'Pending', color: 'blue' },
    timestamp: '5h'
  },
  {
    id: 'r3',
    creator: 'Consistency Team',
    handle: '@team',
    avatarColor: 'violet',
    preview: 'Announcement — New Creator Tier launching next week',
    type: { label: 'Announcement', icon: Megaphone, color: 'violet' },
    status: { label: 'Internal', color: 'violet' },
    timestamp: '1d'
  },
  {
    id: 'r4',
    creator: 'Atlas Studio',
    handle: '@atlasstudio',
    avatarColor: 'blue',
    preview: 'Creator Partnership Invite — long-term content collab',
    type: { label: 'Partnership', icon: Briefcase, color: 'blue' },
    status: { label: 'Priority', color: 'red' },
    timestamp: '1d'
  },
  {
    id: 'r5',
    creator: 'Jordan K.',
    handle: '@jordank',
    avatarColor: 'cyan',
    preview: 'Follow-up — last collab debrief & next steps',
    type: { label: 'Follow-Up', icon: CornerDownRight, color: 'cyan' },
    status: { label: 'Unread', color: 'green' },
    timestamp: '2d'
  },
  {
    id: 'r6',
    creator: 'Sky L.',
    handle: '@skyl',
    avatarColor: 'green',
    preview: 'Feature Request — Auto-clip best moments from live',
    type: { label: 'Feature Request', icon: Star, color: 'amber' },
    status: { label: 'Triaged', color: 'cyan' },
    timestamp: '3d'
  }
];

const InboxRow = ({ row, onSelect }) => {
  const TIcon = row.type.icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(row)}
      className="w-full flex items-center gap-3 px-4 py-2.5 border-b border-white/[0.04] last:border-0 text-left transition-colors hover:bg-white/[0.025] focus:bg-white/[0.04] focus:outline-none"
      data-testid={`creator-inbox-row-${row.id}`}
    >
      <Circle className="h-1.5 w-1.5 fill-purple-400 text-purple-400 shrink-0" />

      <div className={`w-8 h-8 rounded-full bg-${row.avatarColor}-500/15 border border-white/5 flex items-center justify-center shrink-0`}>
        <span className={`text-[10px] font-semibold text-${row.avatarColor}-300`}>
          {row.creator.split(' ').map((s) => s[0]).join('').slice(0, 2)}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-xs font-semibold text-white truncate">
            {row.creator} <span className="text-[#555E6E] font-normal">{row.handle}</span>
          </p>
          <div className={`hidden md:flex items-center gap-1 px-1.5 py-0.5 rounded bg-${row.type.color}-500/10 shrink-0`}>
            <TIcon className={`h-2.5 w-2.5 text-${row.type.color}-400`} />
            <span className={`text-[9px] uppercase tracking-wide text-${row.type.color}-300`}>{row.type.label}</span>
          </div>
        </div>
        <p className="text-xs text-[#8A93A6] truncate leading-snug mt-0.5">{row.preview}</p>
      </div>

      <Badge variant="outline" className={`hidden md:inline-flex text-[10px] border-${row.status.color}-500/30 text-${row.status.color}-300 uppercase tracking-wide shrink-0`}>
        {row.status.label}
      </Badge>

      <span className="text-[10px] text-[#555E6E] tabular-nums shrink-0 w-6 text-right">{row.timestamp}</span>
    </button>
  );
};

export default function ConnectCreatorInbox() {
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const activeFilterLabel = filters.find((f) => f.value === filter)?.label || 'All';

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="connect-creator-inbox-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Inbox className="h-6 w-6 text-purple-400" />
          <h1 className="text-3xl font-bold text-white">Creator Inbox</h1>
          <Badge variant="outline" className="text-[10px] border-purple-500/30 text-purple-300 uppercase tracking-wider font-semibold">
            Internal
          </Badge>
        </div>
        <p className="text-[#8A93A6] max-w-2xl">
          Creator collaboration, internal communication, and ecosystem coordination.
        </p>
      </div>

      {/* Info Card */}
      <div
        className="mb-5 rounded-xl border border-white/5 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-indigo-500/[0.04] px-5 py-3.5"
        data-testid="creator-inbox-info-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-500/[0.08] border border-purple-500/15 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-purple-300" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Internal Creator Communication</p>
            <p className="text-xs text-[#8A93A6] leading-relaxed max-w-3xl">
              Creator Inbox powers collaboration, creator relationships, announcements, and internal communication inside Consistency.
            </p>
          </div>
        </div>
      </div>

      {/* Unified Inbox */}
      <Card className="glass border-white/5 overflow-hidden" data-testid="creator-inbox-list">
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5 bg-[#0F1117]/95 backdrop-blur supports-[backdrop-filter]:bg-[#0F1117]/80">
          <div className="flex items-center gap-2">
            <Inbox className="h-4 w-4 text-purple-400" />
            <span className="text-sm font-semibold text-white">Creator Inbox</span>
            <span className="text-xs text-[#8A93A6]">· {activeFilterLabel}</span>
          </div>

          <div className="flex flex-wrap items-center gap-1" data-testid="creator-inbox-filters">
            {filters.map((f) => {
              const FIcon = f.icon;
              return (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    filter === f.value
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : 'text-[#8A93A6] hover:text-white border border-transparent'
                  }`}
                  data-testid={`creator-inbox-filter-${f.value}`}
                >
                  <FIcon className="h-3.5 w-3.5" />
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dense rows */}
        <div data-testid="creator-inbox-rows">
          {previewRows.map((row) => (
            <InboxRow key={row.id} row={row} onSelect={setSelected} />
          ))}
        </div>

        {/* Compact footer */}
        <div className="border-t border-white/5 px-5 py-4 bg-white/[0.015] flex items-center gap-3">
          <Lock className="h-3.5 w-3.5 text-[#8A93A6] shrink-0" />
          <p className="text-xs text-[#8A93A6]">
            <span className="text-[#C7CED9] font-medium">Read-only preview.</span>{' '}
            Creator DMs, collaboration requests, feature requests, internal announcements, partnership invites, and follow-ups will all flow into this unified inbox.
          </p>
        </div>
      </Card>

      <div className="mt-8 text-center">
        <p className="text-xs text-[#555E6E]">
          Creator relationships strengthen the ecosystem — collaboration creates momentum.
        </p>
      </div>

      {/* Read-only Detail Drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[#0B0D12] border-l border-white/5 text-white" data-testid="creator-inbox-detail-drawer">
          {selected && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded bg-${selected.type.color}-500/10`}>
                    <selected.type.icon className={`h-3 w-3 text-${selected.type.color}-400`} />
                    <span className={`text-[10px] uppercase tracking-wide text-${selected.type.color}-300`}>{selected.type.label}</span>
                  </div>
                  <Badge variant="outline" className={`text-[10px] border-${selected.status.color}-500/30 text-${selected.status.color}-300 uppercase tracking-wide`}>
                    {selected.status.label}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6]">Read-only</Badge>
                </div>
                <SheetTitle className="text-white">{selected.preview}</SheetTitle>
                <SheetDescription className="text-[#8A93A6]">Received {selected.timestamp} ago</SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full bg-${selected.avatarColor}-500/15 border border-white/5 flex items-center justify-center`}>
                    <span className={`text-xs font-semibold text-${selected.avatarColor}-300`}>
                      {selected.creator.split(' ').map((s) => s[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{selected.creator}</p>
                    <p className="text-[11px] text-[#8A93A6]">{selected.handle}</p>
                  </div>
                </div>
                <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4">
                  <p className="text-sm text-[#C7CED9] leading-relaxed">
                    Full message thread, attachments, and reply controls will render here once the creator messaging layer is live.
                  </p>
                </div>
                <p className="text-xs text-[#555E6E] italic">
                  This is a read-only preview. Replies, acceptance, and triage actions will activate in Wave 2.
                </p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
